import { useCallback, useEffect, useState, type FormEvent, type KeyboardEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type {
  MeResponse,
  PeriodeHonor,
  RiwayatAbsensiItem,
  SlipHonorarium,
  UpdateMeRequest,
} from '@app/shared';
import { AdminLayout } from '../components/admin-layout';
import {
  Alert,
  Badge,
  Button,
  Card,
  Input,
  Pagination,
  Select,
  Table,
  Td,
  Th,
} from '../components/ui';
import { useInstitutionBrand } from '../components/institution-brand';
import {
  changeMyPassword,
  getMyPeriodeHonorList,
  getMyProfile,
  getMyRiwayatAbsensi,
  getMySlip,
  updateMyProfile,
} from '../features/auth/profile-api';
import { useAuth } from '../features/auth/use-auth';

type ProfileForm = {
  email: string;
  nomor_telepon: string;
  alamat: string;
  nama_bank: string;
  nomor_rekening: string;
  atas_nama_rekening: string;
};

type Tab = 'profil' | 'absensi' | 'honor';

const empty: ProfileForm = {
  email: '',
  nomor_telepon: '',
  alamat: '',
  nama_bank: '',
  nomor_rekening: '',
  atas_nama_rekening: '',
};

const rupiah = new Intl.NumberFormat('id-ID', {
  style: 'currency',
  currency: 'IDR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const TAB_LABELS: Record<Tab, string> = {
  profil: 'Profil',
  absensi: 'Riwayat Absensi',
  honor: 'Slip Honor',
};

const STATUS_LABELS: Record<string, string> = {
  hadir: 'Hadir',
  izin: 'Izin',
  sakit: 'Sakit',
  alpha: 'Alpha',
};

const STATUS_COLORS: Record<string, 'green' | 'blue' | 'gray' | 'red'> = {
  hadir: 'green',
  izin: 'blue',
  sakit: 'gray',
  alpha: 'red',
};

export function ProfilSayaPage() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const brand = useInstitutionBrand();

  const [activeTab, setActiveTab] = useState<Tab>('profil');
  const [profile, setProfile] = useState<MeResponse | null>(null);
  const [form, setForm] = useState<ProfileForm>(empty);
  const [password, setPassword] = useState({
    password_lama: '',
    password_baru: '',
    konfirmasi: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Riwayat absensi state
  const [riwayat, setRiwayat] = useState<RiwayatAbsensiItem[]>([]);
  const [riwayatPage, setRiwayatPage] = useState(1);
  const [riwayatTotal, setRiwayatTotal] = useState(0);
  const [riwayatLimit] = useState(10);
  const [riwayatLoading, setRiwayatLoading] = useState(false);
  const [riwayatError, setRiwayatError] = useState<string | null>(null);
  const [riwayatStatus, setRiwayatStatus] = useState<string>('');

  // Periode honor state
  const [periodeList, setPeriodeList] = useState<PeriodeHonor[]>([]);
  const [periodeLoading, setPeriodeLoading] = useState(false);
  const [selectedPeriode, setSelectedPeriode] = useState<number | ''>('');
  const [slip, setSlip] = useState<SlipHonorarium | null>(null);
  const [slipLoading, setSlipLoading] = useState(false);
  const [slipError, setSlipError] = useState<string | null>(null);

  const loadProfile = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getMyProfile();
      setProfile(data);
      setForm({
        email: data.akun.email ?? '',
        nomor_telepon: data.pegawai.nomor_telepon ?? '',
        alamat: data.pegawai.alamat ?? '',
        nama_bank: data.pegawai.nama_bank ?? '',
        nomor_rekening: data.pegawai.nomor_rekening ?? '',
        atas_nama_rekening: data.pegawai.atas_nama_rekening ?? '',
      });
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal memuat profil');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRiwayat = useCallback(async () => {
    setRiwayatLoading(true);
    setRiwayatError(null);
    try {
      const data = await getMyRiwayatAbsensi({
        page: riwayatPage,
        limit: riwayatLimit,
        status: riwayatStatus ? (riwayatStatus as 'hadir' | 'izin' | 'sakit' | 'alpha') : undefined,
      });
      setRiwayat(data.rows);
      setRiwayatTotal(data.total);
    } catch (e) {
      setRiwayatError(e instanceof Error ? e.message : 'Gagal memuat riwayat absensi');
    } finally {
      setRiwayatLoading(false);
    }
  }, [riwayatPage, riwayatLimit, riwayatStatus]);

  const loadPeriodeList = useCallback(async () => {
    setPeriodeLoading(true);
    try {
      const { rows } = await getMyPeriodeHonorList({ limit: 50 });
      setPeriodeList(rows);
      if (rows.length > 0 && !selectedPeriode) {
        setSelectedPeriode(rows[0]!.id);
      }
    } catch {
      // Guru/KS might not have periode access — silently ignore
    } finally {
      setPeriodeLoading(false);
    }
  }, [selectedPeriode]);

  const loadSlip = useCallback(async () => {
    if (!selectedPeriode) return;
    setSlipLoading(true);
    setSlipError(null);
    try {
      const data = await getMySlip(Number(selectedPeriode));
      setSlip(data);
    } catch (e) {
      setSlip(null);
      const msg = e instanceof Error ? e.message : 'Gagal memuat slip';
      // Config missing is expected for some pegawai
      if (msg.includes('CONFIG_MISSING') || msg.includes('belum ada')) {
        setSlipError('Konfigurasi honor untuk periode ini belum tersedia.');
      } else {
        setSlipError(msg);
      }
    } finally {
      setSlipLoading(false);
    }
  }, [selectedPeriode]);

  useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  // Check hash for password section
  useEffect(() => {
    if (location.hash === '#password') {
      const el = document.getElementById('password');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  }, [location.hash]);

  // Load riwayat when tab is active
  useEffect(() => {
    if (activeTab === 'absensi') void loadRiwayat();
  }, [activeTab, loadRiwayat]);

  // Load periode list when honor tab is active
  useEffect(() => {
    if (activeTab === 'honor' && periodeList.length === 0) void loadPeriodeList();
  }, [activeTab, loadPeriodeList, periodeList.length]);

  // Load slip when periode is selected
  useEffect(() => {
    if (activeTab === 'honor' && selectedPeriode) void loadSlip();
  }, [activeTab, selectedPeriode, loadSlip]);

  const save = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setMessage(null);
    setError(null);
    const nullable = (value: string) => value.trim() || null;
    const body: UpdateMeRequest = {
      email: nullable(form.email),
      nomor_telepon: nullable(form.nomor_telepon),
      alamat: nullable(form.alamat),
      nama_bank: nullable(form.nama_bank),
      nomor_rekening: nullable(form.nomor_rekening),
      atas_nama_rekening: nullable(form.atas_nama_rekening),
    };
    try {
      await updateMyProfile(body);
      setMessage('Profil berhasil diperbarui.');
      await loadProfile();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal memperbarui profil');
    } finally {
      setSaving(false);
    }
  };

  const savePassword = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setMessage(null);
    if (password.password_baru !== password.konfirmasi) {
      setError('Konfirmasi password tidak sama.');
      return;
    }
    setSaving(true);
    try {
      await changeMyPassword({
        password_lama: password.password_lama,
        password_baru: password.password_baru,
      });
      await logout();
      navigate('/login', { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal mengubah password');
    } finally {
      setSaving(false);
    }
  };

  const copyAccount = async () => {
    if (!form.nomor_rekening) return;
    try {
      await navigator.clipboard.writeText(form.nomor_rekening);
      setMessage('Nomor rekening disalin.');
    } catch {
      setError('Clipboard tidak tersedia. Salin nomor rekening secara manual.');
    }
  };

  const handleTabKey = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      const tabs: Tab[] = ['profil', 'absensi', 'honor'];
      const currentIndex = tabs.indexOf(activeTab);
      const direction = event.key === 'ArrowRight' ? 1 : -1;
      const nextIndex = (currentIndex + direction + tabs.length) % tabs.length;
      const nextTab = tabs[nextIndex];
      if (nextTab) setActiveTab(nextTab);
      // Focus the new tab button
      const tabButton = document.querySelector(
        `[data-tab="${tabs[nextIndex]}"]`,
      ) as HTMLButtonElement | null;
      tabButton?.focus();
    }
  };

  const riwayatTotalPages = Math.ceil(riwayatTotal / riwayatLimit) || 1;

  if (loading) {
    return (
      <AdminLayout title="Profil Saya">
        <Card>Memuat profil…</Card>
      </AdminLayout>
    );
  }

  const p = profile?.pegawai;

  return (
    <AdminLayout title="Profil Saya">
      {error && <Alert type="error">{error}</Alert>}
      {message && <Alert type="success">{message}</Alert>}

      {/* Tab navigation */}
      <div
        role="tablist"
        aria-label="Bagian Profil Saya"
        onKeyDown={handleTabKey}
        style={{
          display: 'flex',
          gap: '0.25rem',
          borderBottom: '2px solid #E2E8F0',
          marginBottom: '1rem',
          flexWrap: 'wrap',
        }}
      >
        {(['profil', 'absensi', 'honor'] as Tab[]).map((tab) => (
          <button
            key={tab}
            data-tab={tab}
            role="tab"
            aria-selected={activeTab === tab}
            aria-controls={`panel-${tab}`}
            id={`tab-${tab}`}
            tabIndex={activeTab === tab ? 0 : -1}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '0.625rem 1rem',
              border: 'none',
              borderBottom: activeTab === tab ? '3px solid #2563EB' : '3px solid transparent',
              background: 'transparent',
              color: activeTab === tab ? '#2563EB' : '#64748B',
              fontWeight: activeTab === tab ? 600 : 500,
              cursor: 'pointer',
              fontSize: '0.9rem',
              marginBottom: '-2px',
              borderRadius: '0.375rem 0.375rem 0 0',
            }}
          >
            {TAB_LABELS[tab]}
          </button>
        ))}
      </div>

      {/* Profile tab */}
      {activeTab === 'profil' && (
        <div
          role="tabpanel"
          id="panel-profil"
          aria-labelledby="tab-profil"
          style={{ display: 'grid', gap: '1rem', maxWidth: 900 }}
        >
          <Card>
            <h2 style={{ marginTop: 0 }}>Data Akun</h2>
            <div className="form-grid">
              <Input label="Username" value={profile?.akun.username ?? ''} disabled />
              <Input
                label="Email"
                name="email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            {profile?.roles && profile.roles.length > 0 && (
              <div style={{ marginTop: '0.5rem' }}>
                <span style={{ fontSize: '0.875rem', fontWeight: 500, marginRight: '0.5rem' }}>
                  Peran:
                </span>
                {profile.roles.map((role) => (
                  <Badge key={role} color="blue">
                    {role === 'admin' ? 'Admin' : role === 'guru' ? 'Guru' : 'Kepala Sekolah'}
                  </Badge>
                ))}
              </div>
            )}
            {profile?.penugasan_sekolah && profile.penugasan_sekolah.length > 0 && (
              <div style={{ marginTop: '0.5rem' }}>
                <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>Penugasan Sekolah:</span>
                <ul
                  style={{
                    margin: '0.25rem 0 0 1.25rem',
                    padding: 0,
                    fontSize: '0.875rem',
                    color: '#475569',
                  }}
                >
                  {profile.penugasan_sekolah.map((ps) => (
                    <li key={ps.sekolah_id}>
                      {ps.nama} ({ps.kode}) —{' '}
                      <Badge color={ps.status_penugasan === 'aktif' ? 'green' : 'gray'}>
                        {ps.status_penugasan}
                      </Badge>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Card>
          <Card>
            <h2 style={{ marginTop: 0 }}>Data Pribadi</h2>
            <div className="form-grid">
              <Input label="Nama Lengkap" value={p?.nama ?? ''} disabled />
              <Input label="NIP/NIK" value={p?.nip_nik ?? ''} disabled />
              <Input label="Tempat Lahir" value={p?.tempat_lahir ?? '-'} disabled />
              <Input label="Tanggal Lahir" value={p?.tanggal_lahir ?? '-'} disabled />
              <Input
                label="Jenis Kelamin"
                value={p?.jenis_kelamin === 'L' ? 'Laki-laki' : 'Perempuan'}
                disabled
              />
              <Input label="Status Kepegawaian" value={p?.status_kepegawaian ?? '-'} disabled />
              <Input
                label="Nomor HP"
                name="nomor_telepon"
                value={form.nomor_telepon}
                onChange={(e) => setForm({ ...form, nomor_telepon: e.target.value })}
              />
              <Input
                label="Alamat"
                name="alamat"
                value={form.alamat}
                onChange={(e) => setForm({ ...form, alamat: e.target.value })}
              />
            </div>
          </Card>
          <Card>
            <h2 style={{ marginTop: 0 }}>Data Bank</h2>
            <form onSubmit={save}>
              <div className="form-grid">
                <Input
                  label="Nama Bank"
                  name="nama_bank"
                  value={form.nama_bank}
                  onChange={(e) => setForm({ ...form, nama_bank: e.target.value })}
                />
                <Input
                  label="Atas Nama Rekening"
                  name="atas_nama_rekening"
                  value={form.atas_nama_rekening}
                  onChange={(e) => setForm({ ...form, atas_nama_rekening: e.target.value })}
                />
              </div>
              <div className="responsive-actions">
                <div style={{ flex: '1 1 260px' }}>
                  <Input
                    label="Nomor Rekening"
                    name="nomor_rekening"
                    value={form.nomor_rekening}
                    onChange={(e) => setForm({ ...form, nomor_rekening: e.target.value })}
                  />
                </div>
                <Button type="button" variant="secondary" onClick={() => void copyAccount()}>
                  Salin
                </Button>
              </div>
              <Button type="submit" disabled={saving}>
                Simpan Profil
              </Button>
            </form>
          </Card>
          <Card>
            <h2 id="password" style={{ marginTop: 0 }}>
              Ubah Password
            </h2>
            <form onSubmit={savePassword}>
              <Input
                label="Password Lama"
                name="password_lama"
                type="password"
                autoComplete="current-password"
                value={password.password_lama}
                onChange={(e) => setPassword({ ...password, password_lama: e.target.value })}
                required
              />
              <Input
                label="Password Baru"
                name="password_baru"
                type="password"
                minLength={8}
                autoComplete="new-password"
                value={password.password_baru}
                onChange={(e) => setPassword({ ...password, password_baru: e.target.value })}
                required
              />
              <Input
                label="Konfirmasi Password Baru"
                name="konfirmasi"
                type="password"
                minLength={8}
                autoComplete="new-password"
                value={password.konfirmasi}
                onChange={(e) => setPassword({ ...password, konfirmasi: e.target.value })}
                required
              />
              <p style={{ fontSize: '0.8rem', color: '#64748B', margin: '0 0 0.75rem' }}>
                Setelah mengubah password, semua sesi akan berakhir dan Anda perlu login kembali.
              </p>
              <Button type="submit" disabled={saving}>
                Ubah Password
              </Button>
            </form>
          </Card>
        </div>
      )}

      {/* Absensi tab */}
      {activeTab === 'absensi' && (
        <div role="tabpanel" id="panel-absensi" aria-labelledby="tab-absensi">
          <Card>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '0.5rem',
                marginBottom: '1rem',
              }}
            >
              <h2 style={{ margin: 0 }}>Riwayat Absensi Saya</h2>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <Select
                  label="Filter Status"
                  value={riwayatStatus}
                  onChange={(e) => {
                    setRiwayatStatus(e.target.value);
                    setRiwayatPage(1);
                  }}
                  style={{ width: 'auto', minWidth: 150 }}
                >
                  <option value="">Semua</option>
                  <option value="hadir">Hadir</option>
                  <option value="izin">Izin</option>
                  <option value="sakit">Sakit</option>
                  <option value="alpha">Alpha</option>
                </Select>
              </div>
            </div>

            {riwayatError && <Alert type="error">{riwayatError}</Alert>}

            {riwayatLoading ? (
              <p>Memuat riwayat…</p>
            ) : riwayat.length === 0 ? (
              <p style={{ color: '#64748B' }}>Belum ada riwayat absensi.</p>
            ) : (
              <>
                <Table>
                  <thead>
                    <tr>
                      <Th>Tanggal</Th>
                      <Th>Hari</Th>
                      <Th>Sekolah</Th>
                      <Th>Status</Th>
                      <Th>Check In</Th>
                      <Th>Check Out</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {riwayat.map((row) => (
                      <tr key={row.id}>
                        <Td>{row.tanggal}</Td>
                        <Td>{row.hari}</Td>
                        <Td>{row.sekolahNama}</Td>
                        <Td>
                          <Badge color={STATUS_COLORS[row.status] ?? 'gray'}>
                            {STATUS_LABELS[row.status] ?? row.status}
                          </Badge>
                        </Td>
                        <Td>{row.checkInAt ?? '-'}</Td>
                        <Td>{row.checkOutAt ?? '-'}</Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                <Pagination
                  page={riwayatPage}
                  totalPages={riwayatTotalPages}
                  onChange={setRiwayatPage}
                />
              </>
            )}
          </Card>
        </div>
      )}

      {/* Honor tab */}
      {activeTab === 'honor' && (
        <div
          role="tabpanel"
          id="panel-honor"
          aria-labelledby="tab-honor"
          style={{ display: 'grid', gap: '1rem' }}
        >
          <Card>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-end',
                flexWrap: 'wrap',
                gap: '0.5rem',
              }}
            >
              <h2 style={{ margin: 0 }}>Slip Honorarium Saya</h2>
              <Select
                label="Pilih Periode"
                value={selectedPeriode}
                onChange={(e) => setSelectedPeriode(e.target.value ? Number(e.target.value) : '')}
                style={{ width: 'auto', minWidth: 200 }}
                disabled={periodeLoading || periodeList.length === 0}
              >
                {periodeList.length === 0 && !periodeLoading && (
                  <option value="">Belum ada periode</option>
                )}
                {periodeList.map((ph) => (
                  <option key={ph.id} value={ph.id}>
                    {ph.kode_periode} ({ph.status})
                  </option>
                ))}
              </Select>
            </div>
          </Card>

          {slipError && <Alert type="error">{slipError}</Alert>}

          {slipLoading ? (
            <Card>
              <p>Memuat slip…</p>
            </Card>
          ) : slip ? (
            <Card>
              <div
                className="no-print"
                style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem' }}
              >
                <Button onClick={() => window.print()}>Cetak Slip</Button>
              </div>

              <div
                style={{
                  textAlign: 'center',
                  marginBottom: '1.5rem',
                  borderBottom: '2px solid #1e293b',
                  paddingBottom: '1rem',
                }}
              >
                <h2 style={{ margin: 0 }}>
                  {brand.nama_pesantren || 'Pesantren Madinah Ar-Rasul'}
                </h2>
                <p style={{ margin: '0.25rem 0 0', color: '#64748b' }}>Slip Honorarium Pegawai</p>
              </div>

              <div style={{ display: 'grid', gap: '1.5rem' }}>
                <section>
                  <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem' }}>Informasi Pegawai</h3>
                  <SlipRow label="Nama Pegawai" value={slip.pegawai_nama} />
                  <SlipRow label="NIP/NIK" value={slip.pegawai_nip_nik || '-'} />
                  <SlipRow label="Sekolah" value={slip.sekolah_nama || '-'} />
                  <SlipRow label="Jabatan" value={slip.jabatan_nama || '-'} />
                  <SlipRow label="Periode Honor" value={slip.kode_periode} />
                  <SlipRow
                    label="Status Slip"
                    value={
                      <Badge color={slip.status_slip === 'final' ? 'red' : 'gray'}>
                        {slip.status_slip === 'final' ? 'Final' : 'Draft'}
                      </Badge>
                    }
                  />
                </section>

                <section>
                  <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem' }}>Rincian Honor</h3>
                  <SlipRow label="Total JP" value={slip.total_jp.toLocaleString('id-ID')} />
                  <SlipRow label="Tarif per JP" value={rupiah.format(slip.tarif_per_jp)} />
                  <SlipRow label="Honor JP" value={rupiah.format(slip.honor_jp)} />
                  <SlipRow label="Hari Hadir" value={slip.hari_hadir.toLocaleString('id-ID')} />
                  <SlipRow
                    label="Tarif Kehadiran per Hari"
                    value={rupiah.format(slip.tarif_kehadiran)}
                  />
                  <SlipRow label="Honor Kehadiran" value={rupiah.format(slip.honor_kehadiran)} />
                  <SlipRow label="Honor Jabatan" value={rupiah.format(slip.honor_jabatan)} />
                  <SlipRow label="Honor Wali Kelas" value={rupiah.format(slip.honor_wali_kelas)} />
                </section>

                {Array.isArray(slip.insentif) && slip.insentif.length > 0 && (
                  <section>
                    <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem' }}>Insentif Kegiatan</h3>
                    <div
                      style={{
                        border: '1px solid #e2e8f0',
                        borderRadius: '0.375rem',
                        overflow: 'hidden',
                      }}
                    >
                      {slip.insentif.map((item, idx) => (
                        <div
                          key={idx}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            padding: '0.5rem 0.75rem',
                            borderBottom:
                              idx < slip.insentif.length - 1 ? '1px solid #e2e8f0' : undefined,
                            background: idx % 2 === 0 ? '#f8fafc' : '#ffffff',
                          }}
                        >
                          <span>{item.nama_kegiatan || `Insentif ${idx + 1}`}</span>
                          <span>{rupiah.format(Number(item.nominal) || 0)}</span>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                <section
                  style={{
                    marginTop: '0.5rem',
                    padding: '1rem',
                    background: '#f1f5f9',
                    borderRadius: '0.375rem',
                  }}
                >
                  <SlipRow label="Total Honor" value={rupiah.format(slip.total_honor)} bold />
                </section>
              </div>

              <p
                style={{
                  marginTop: '2rem',
                  textAlign: 'center',
                  fontSize: '0.875rem',
                  color: '#64748b',
                }}
              >
                Slip ini merupakan hasil perhitungan honorarium dan tidak dapat diubah nilainya.
              </p>

              <style>{`
                @media print {
                  .no-print {
                    display: none !important;
                  }
                  body {
                    background: #fff;
                  }
                }
              `}</style>
            </Card>
          ) : !slipError ? (
            <Card>
              <p style={{ color: '#64748B' }}>Pilih periode untuk melihat slip honorarium.</p>
            </Card>
          ) : null}
        </div>
      )}

      {/* User info footer */}
      <div style={{ marginTop: '1rem', fontSize: '0.75rem', color: '#94A3B8' }}>
        Login sebagai: {user?.nama ?? 'Pengguna'} ({user?.username ?? ''})
      </div>
    </AdminLayout>
  );
}

function SlipRow({
  label,
  value,
  bold = false,
}: {
  label: string;
  value: React.ReactNode;
  bold?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: '0.5rem 0',
        borderBottom: '1px solid #e2e8f0',
        fontWeight: bold ? 600 : 400,
      }}
    >
      <span style={{ color: '#475569' }}>{label}</span>
      <span>{value}</span>
    </div>
  );
}
